import css_6exyd9vdff from './css/MediaGroup.css';
import Div from './Div.js';
import GetPrivateData from '../Classes/GetPrivateData.js';
import ContentPlayer from './ContentPlayer.js';
import AdPlayer from './AdPlayer.js';
import makeAdTag from '../Functions/makeAdTag.js';
import Spinner from './Spinner.js';
import dateStringToUTCDate from '../Functions/dateStringToUTCDate.js';

const css = css_6exyd9vdff;

/** @namespace videojs.controlBar.fullscreenToggle.handleClick */
/** @namespace userActions.doubleClick */
/** @namespace config */

export default class MediaGroup extends Div
{
	constructor(parentElement)
	{
		super(parentElement);

		const privateData =
		{
			mediaGroupActive: false,
			hasStarted: false,
			playing: false,
			adsActive: false
		};

		getPrivateData.for(this,privateData);

		redispatchEvents.call(this);

		this.contentPlayer = new ContentPlayer(this);
		this.spinner = new Spinner(this);
		this.adPlayer = new AdPlayer(this,this.contentPlayer);

		Promise.all
		(
			[
				new Promise(resolve => this.contentPlayer.element.addEventListener('ready',resolve)),
				new Promise(resolve => this.adPlayer.element.addEventListener('ready',resolve))
			]
		)
		.then
		(
			() =>
			{
				this.dispatchEvent('ready');
			}
		);

		this.element.addEventListener
		(
			// when the ad sdk is telling us it is going to play an ad
			'adActive', () =>
			{
				// mark the ad player as in control
				privateData.adsActive = true;
				privateData.adsActivated = true;

				// if content is playing, mark a transition so we cancel extra pause events
				if(privateData.contentPlaying)
				{
					privateData.adTransition = true;
				}
				// and then pause the content player
				privateData.contentLoad.then
				(
					() =>
					{
						this.contentPlayer.playing(false);
					},
				)
			}
		);

		this.element.addEventListener
		(
			// when the ad ask says it is finished
			'adInactive', () =>
			{
				// mark the content player as in control
				privateData.adsActive = false;

				// if content is done, then so are we
				if(privateData.contentComplete)
				{
					return;
				}

				// sometimes the sdk sends inactive events without sending active events
				// if a playing(true) command has been given, then resume playing again
				if(privateData.hasStarted)
				{
					privateData.contentLoad.then
					(
						() =>
						{
							this.contentPlayer.playing(true);
						}
					)
				}
			}
		);

		this.element.addEventListener
		(
			// the sdk is telling us that an ad has played, either through a start event or a resume event
			'adPlay', () =>
			{
				// if the ad player is not in charge, log a warning and do nothing
				if(!privateData.adsActive)
				{
					// console.warn('ad playing while not active');
					return;
				}

				// if the ad player is already playing, log a warning and do nothing
				if(privateData.adPlaying)
				{
					// console.warn('ad playing while already playing');
					return;
				}

				// record the play state
				privateData.adPlaying = true;

				// if we are not transitioning between content and ad, send a mediaGroup event
				if(!privateData.adTransition)
				{
					this.dispatchEvent('play');
				}
				privateData.adTransition = false;
			}
		);

		// the sdk has paused the ad
		// same logic as adPlay
		this.element.addEventListener
		(
			'adPause', () =>
			{
				if (!privateData.adsActive)
				{
					// console.warn('ad paused while not active');
					return;
				}

				if (!privateData.adPlaying)
				{
					// console.warn('ad paused while already paused');
					return;
				}

				privateData.adPlaying = false;
				if (!privateData.adTransition)
				{
					this.dispatchEvent('pause');
				}
			}
		);

		// the ad has finished playing. single ad in an ad pod or ad outside of an ad pod
		this.element.addEventListener
		(
			'adFinish', () =>
			{
				if (!privateData.adsActive)
				{
					// console.warn('ad finished while not active');
					return;
				}

				if (!privateData.adPlaying)
				{
					// console.warn('ad finished while already paused');
					return;
				}

				privateData.adTransition = true;
			}
		);

		this.element.addEventListener
		(
			'adComplete', () =>
			{
				privateData.adsComplete = true;

				if(privateData.contentComplete)
				{
					setTimeout(() => this.dispatchEvent('complete'));
				}

				if(privateData.adsActive)
				{
					console.error('ima sdk bug detected, probably a 400 error condition, attempting to bypass');
					this.dispatchEvent('adInactive');
				}
			}
		);

		this.element.addEventListener
		(
			'contentPlay', () =>
			{
				if (privateData.adsActive)
				{
					// content isn't allowed to play while the ad system is in charge.
					// if something has caused it to play, pause it
					// console.warn('content play while ads active, pausing');
					privateData.contentLoad.then
					(
						() => this.contentPlayer.playing(false)
					);
					return;
				}

				if (privateData.contentPlaying)
				{
					// console.warn('content playing while already playing');
					return;
				}

				privateData.contentPlaying = true;
				if(!privateData.adTransition)
				{
					this.dispatchEvent('play');
				}
				privateData.adTransition = false;
			}
		);

		this.element.addEventListener
		(
			'contentPause', event =>
			{
				if (privateData.adsActive && !privateData.adTransition)
				{
					// console.warn('content paused while ads active');
					return;
				}

				if (!privateData.contentPlaying)
				{
					// console.warn('content paused while already paused');
					return;
				}

				privateData.contentPlaying = false;

				if (privateData.adsActivated && !privateData.adsComplete && event.detail && event.detail.ended)
				{
					privateData.adTransition = true;
				}

				if (!privateData.adTransition)
				{
					this.dispatchEvent('pause', {detail: event.detail});
				}
			}
		);

		this.element.addEventListener
		(
			'contentLoadError', (e) =>
			{
				this.dispatchEvent('contentFinish');
			}
		);

		this.element.addEventListener
		(
			'contentFinish', () =>
			{
				privateData.contentComplete = true;
				if(privateData.adsComplete)
				{
					setTimeout(() => this.dispatchEvent('complete'));
				}
				else
				{
					privateData.adLoad.then
					(
						() =>
						{
							this.adPlayer.adsLoader &&
							this.adPlayer.adsLoader.contentComplete();
						},
						() => {}
					)
				}
			}
		);
	}

	get cssText()
	{
		return css;
	}

	active(value)
	{
		const privateData = getPrivateData.for(this);

		if(arguments.length === 0)
		{
			return privateData.mediaGroupActive;
		}

		if(value === privateData.mediaGroupActive)
		{
			return;
		}

		privateData.mediaGroupActive = !!value;

		if(value)
		{
			this.element.classList.add('active');
			this.dispatchEvent('active');
		}
		else
		{
			this.element.classList.remove('active');
			this.dispatchEvent('inactive');
			// unload by load(undefined)
			this.load();
		}
	}

	muted(...args)
	{
		return this.contentPlayer.muted(...args);
	}

	volume(...args)
	{
		return this.contentPlayer.volume(...args);
	}

	load(index,usingAltPlaylist)
	{
		const privateData = getPrivateData.for(this);

		privateData.liveInterval && clearInterval(privateData.liveInterval);

		if(!index && index !== 0)
		{
			// unload / reset
			this.playing(false);
			return;
		}

		const playlistItem = (usingAltPlaylist?config.feed.ESG_altPlaylist:config.playlist.items)[index];
		const poster = playlistItem['configuration'].poster;
		const sources = [];
		let a = document.createElement('a');
		a.href = playlistItem['configuration'].sources.src;
		a.protocol = 'https';
		if(config.capabilities.hls)
		{
			sources.push
			(
				{
					type: 'application/x-mpegURL',
					src: a.href
				}
			);
		}
		a.pathname = playlistItem['videoConversions']['MP4300k'];
		sources.push
		(
			{
				type: 'video/mp4',
				src: a.href
			}
		);
		a = null;

		const liveFeed = (playlistItem['configuration']||[])['liveFeed'];
		if(liveFeed)
		{
			const now = Date.now();
			const startDate = dateStringToUTCDate(liveFeed.startTime);
			const started =  startDate <= now;
			let endDate = dateStringToUTCDate(liveFeed.endTime);
			let ended = endDate <= now;
			const updateBeatLength = 60/5;
			let updateBeat = -1;

			const updateLive = () =>
			{
				const now = Date.now();
				ended = endDate <= now;
				if(ended)
				{
					clearInterval(privateData.liveInterval);
					this.dispatchEvent('complete');
				}
				else
				{
					updateBeat = ++updateBeat % updateBeatLength;
					if(updateBeat === 0)
					{
						fetch(`${config.updateLocation}?q=liveFeed&sm_id=${playlistItem.SM_ID}`)
						.then(v => v.json())
						.then
						(
							v =>
							{
								endDate = v['endDate'] || null;
								liveFeed.endTime = new Date(dateStringToUTCDate(endDate)).toISOString().replace(/T|\..*/g,' ').trim();
							}
						);
					}
				}
			};

			if( started && !ended )
			{
				sources.splice(0,null,liveFeed.sources);
				privateData.liveInterval = setInterval(updateLive,5000);
				updateLive();
			}
		}

		privateData.contentLoad = config.scripts.videojs.then
		(
			() => this.contentPlayer.load(sources,poster)
		);

		privateData.adLoad = config.scripts.ima.then
		(
			() =>
			{
				if(!playlistItem['configuration']['adschedule'])
				{
					const error = 'no ad schedule configured';
					const player = config.divTag;
					const feed = config.feed;
					throw {error,player,feed,playlistItem};
				}

				return this.adPlayer.load
				(
					() => makeAdTag
					(
						playlistItem['configuration']['adschedule'][config.feed.adPlacement].tag,
						this.parentElement.element,
						playlistItem['SM_ID'],
						playlistItem['C_ID'], // This is a Content ID, not a Customer ID
						config.key,
						config.playerStatus.dfpCode,
						config.ac_id,
						'en'
					)
				)
			}
		);

		// has the ad system been activated by the sdk
		privateData.adsActivated = false;
		// is the adPlayer active or the contentPlayer active
		privateData.adsActive = false;

		privateData.adTransition = false;
		privateData.contentPlaying = false;
		privateData.adPlaying = false;
		privateData.hasStarted = false;
		privateData.contentComplete = false;

		privateData.adLoad.then
		(
			() =>
			{
				privateData.adsComplete = false;
				privateData.adsActive = true;
			},
			e =>
			{
				console.warn('Ad Failed to Load:', e);
				privateData.adsComplete = true;
			}
		);
	}

	playing(value)
	{
		const privateData = getPrivateData.for(this);

		if (arguments.length === 0)
		{
			return privateData.adsActive ? privateData.adPlaying : privateData.contentPlaying
		}

		return privateData.adLoad.then
		(
			() =>
			{
				if(!privateData.adsActive)
				{
					return Promise.reject('ads isn\'t active');
				}
				privateData.hasStarted = privateData.hasStarted || value;
				return this.adPlayer.playing(value);
			}
		)
		.catch
		(
			reason =>
			{
				if(privateData.adsActive)
				{
					console.error(reason);
					privateData.adsActive = false;
				}
				return privateData.contentLoad.then
				(
					() =>
					{
						return this.contentPlayer.playing(value)
					}
				)
			}
		);
	}
}

MediaGroup.eventList = ['ready','complete','active','inactive','play','pause'];

const getPrivateData = new GetPrivateData(MediaGroup);

function redispatchEvents()
{
	[...new Set([...MediaGroup.eventList,...ContentPlayer.eventList,...AdPlayer.eventList])]
	.forEach
	(
		type =>
		{
			this.element.addEventListener
			(
				type,
				event =>
				{
					event.stopPropagation();
					let eventSource = 'mediaGroup';
					if(this.contentPlayer.element.contains(event.target))
					{
						eventSource = 'content';
					}
					else if(this.adPlayer.element.contains(event.target))
					{
						eventSource = 'ad';
					}

					const eventType = event.type[0].toUpperCase() + event.type.slice(1);
					this.dispatchEvent(`${eventSource}${eventType}`,{detail:event.detail});
				}
			)
		}
	);
}

